import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import useFirebaseCache from '../hooks/useFirebaseCache.js';

// Create the context
const CollectionContext = createContext(null);

// Custom hook to use the collection context
export const useCollections = () => {
  const context = useContext(CollectionContext);
  if (!context) {
    throw new Error('useCollections must be used within a CollectionProvider');
  }
  return context;
};

// Provider component
export const CollectionProvider = ({ children, user }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { 
    getCachedData,
    setCachedData
  } = useFirebaseCache();

  // Load user collections (offline-only implementation due to security rules)
  const loadUserCollections = useCallback(async () => {
    if (!user) {
      setCollections([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    const cachedCollections = getCachedData(`userCollections_${user.uid}`, []);
    
    try {
      // COMPLETE WORKAROUND: Firestore security rules don't have permissions for collections
      // Always use cached collections or empty array, completely avoiding Firestore queries
      console.log('Using collections offline mode due to security rule limitations');
      
      if (cachedCollections.length) {
        console.log(`Loaded ${cachedCollections.length} collections from cache`);
        setCollections(cachedCollections);
      } else {
        console.log('No cached collections, initializing with empty array');
        // Initialize with empty array
        setCollections([]);
        
        // Create a placeholder collection if none exists to improve UX
        const placeholderCollection = {
          id: 'default-collection',
          name: 'My Collection',
          description: 'A collection of your favorite recipes',
          recipes: []
        };
        
        // Save the placeholder to localStorage
        setCachedData(`userCollections_${user.uid}`, [placeholderCollection]);
        setCollections([placeholderCollection]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error in offline collections handling:', error);
      setCollections([]);
      setLoading(false);
    }
  }, [user, getCachedData, setCachedData]);

  // Create a new collection
  const createCollection = useCallback((collectionData) => {
    if (!user) return null;
    
    try {
      const newCollection = {
        ...collectionData,
        id: `collection-${Date.now()}`,
        recipes: collectionData.recipes || [],
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      };
      
      setCollections(prevCollections => [...prevCollections, newCollection]);
      
      // Update localStorage
      setCachedData(`userCollections_${user.uid}`, [...collections, newCollection]);
      
      return newCollection;
    } catch (error) {
      console.error('Error creating collection:', error);
      return null;
    }
  }, [collections, setCachedData, user]);

  // Update a collection
  const updateCollection = useCallback((collectionId, updatedData) => {
    if (!user) return false;
    
    try {
      const updatedCollections = collections.map(collection => 
        collection.id === collectionId 
          ? { 
              ...collection, 
              ...updatedData, 
              updated: new Date().toISOString() 
            } 
          : collection
      );
      
      setCollections(updatedCollections);
      
      // Update localStorage
      setCachedData(`userCollections_${user.uid}`, updatedCollections);
      
      return true;
    } catch (error) {
      console.error('Error updating collection:', error);
      return false;
    }
  }, [collections, setCachedData, user]);

  // Delete a collection
  const deleteCollection = useCallback((collectionId) => {
    if (!user) return false;
    
    try {
      const filteredCollections = collections.filter(c => c.id !== collectionId);
      
      setCollections(filteredCollections);
      
      // Update localStorage
      setCachedData(`userCollections_${user.uid}`, filteredCollections);
      
      return true;
    } catch (error) {
      console.error('Error deleting collection:', error);
      return false;
    }
  }, [collections, setCachedData, user]);

  // Add a recipe to a collection
  const addRecipeToCollection = useCallback((collectionId, recipeId) => {
    if (!user) return false;
    
    try {
      const collection = collections.find(c => c.id === collectionId);
      
      if (!collection) {
        return false;
      }
      
      // Check if recipe is already in the collection
      if (collection.recipes.includes(recipeId)) {
        return true; // Already exists, consider it a success
      }
      
      const updatedCollection = {
        ...collection,
        recipes: [...collection.recipes, recipeId],
        updated: new Date().toISOString()
      };
      
      const updatedCollections = collections.map(c => 
        c.id === collectionId ? updatedCollection : c
      );
      
      setCollections(updatedCollections);
      
      // Update localStorage
      setCachedData(`userCollections_${user.uid}`, updatedCollections);
      
      return true;
    } catch (error) {
      console.error('Error adding recipe to collection:', error);
      return false;
    }
  }, [collections, setCachedData, user]);

  // Remove a recipe from a collection
  const removeRecipeFromCollection = useCallback((collectionId, recipeId) => {
    if (!user) return false;
    
    try {
      const collection = collections.find(c => c.id === collectionId);
      
      if (!collection) {
        return false;
      }
      
      const updatedCollection = {
        ...collection,
        recipes: collection.recipes.filter(id => id !== recipeId),
        updated: new Date().toISOString()
      };
      
      const updatedCollections = collections.map(c => 
        c.id === collectionId ? updatedCollection : c
      );
      
      setCollections(updatedCollections);
      
      // Update localStorage
      setCachedData(`userCollections_${user.uid}`, updatedCollections);
      
      return true;
    } catch (error) {
      console.error('Error removing recipe from collection:', error);
      return false;
    }
  }, [collections, setCachedData, user]);

  // Load data when user changes
  useEffect(() => {
    if (user) {
      loadUserCollections();
    } else {
      setCollections([]);
      setLoading(false);
    }
  }, [user, loadUserCollections]);

  // Create the context value object
  const collectionContextValue = {
    collections,
    loading,
    createCollection,
    updateCollection,
    deleteCollection,
    addRecipeToCollection,
    removeRecipeFromCollection,
    refreshCollections: loadUserCollections
  };
  
  // Provide the context value to children
  return (
    <CollectionContext.Provider value={collectionContextValue}>
      {children}
    </CollectionContext.Provider>
  );
}; 